import { useState, useEffect } from "react";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Divider,} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { getSelectedTrip } from "../../utils/voyageSelection";

const logEntries = [
  "First line ashore",
  "All fast",
  "Connected",
  "Commenced Load",
  "Stop",
  "Start",
  "Stop",
  "Start",
  "Stop",
  "Start",
  "Stop",
  "Start",
  "Completed Load",
  "Tanks insp / appr",
  "Ull / Sampl / Calc",
  "Disconnected",
  "Barge clear",
];

const BargeLogModal = ({ open, handleClose, bargeLog, readOnly }) => {
  const { currentVoyage, updateTrip, selectedTripId } = useVoyageContextV2();
  const trip =
    getSelectedTrip(currentVoyage, selectedTripId) ||
    Object.values(currentVoyage?.trips || {})[0] ||
    null;

  const [formData, setFormData] = useState({
    logEntries: logEntries.map((entry) => ({
      name: entry,
      date: "",
      localTime: "",
      remarks: "",
    })),
    overallRemarks: "",
  });

  useEffect(() => {
    if (bargeLog) {
      setFormData(bargeLog);
    } else {
      setFormData({
        logEntries: logEntries.map((entry) => ({
          name: entry,
          date: "",
          localTime: "",
          remarks: "",
        })),
        overallRemarks: "",
      });
    }
  }, [bargeLog]);

  const handleChange = (index, field, value) => {
    const updatedEntries = [...formData.logEntries];
    updatedEntries[index][field] = value;
    setFormData({ ...formData, logEntries: updatedEntries });
  };

  const handleSave = async () => {
    if (readOnly) return;
    const tripId = trip?.tripId || selectedTripId;
    if (!tripId) return;
    const existingLogs = Array.isArray(trip.bargeLogs) ? trip.bargeLogs : [];
    let updatedBargeLogs = existingLogs;

    if (bargeLog) {
      // Edit existing log
      updatedBargeLogs = existingLogs.map((log) =>
        log.bargeLogId === bargeLog.bargeLogId ? { ...log, ...formData } : log
      );
    } else {
      // Create new log
      updatedBargeLogs = [
        ...existingLogs,
        { ...formData, bargeLogId: Date.now().toString() },
      ];
    }

    await updateTrip(
      tripId,
      { bargeLogs: updatedBargeLogs },
      {
        summary: bargeLog ? "Updated barge log" : "Created barge log",
      }
    );
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{bargeLog ? "Edit Barge Log" : "New Barge Log"}</DialogTitle>
      <DialogContent>
        {formData.logEntries.map((entry, index) => (
          <div key={index}>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              {entry.name}
            </Typography>
            <Grid container spacing={2}>
              {/* Date */}
              <Grid size={5}>
                <DatePicker
                  label="Date"
                  value={entry.date ? dayjs(entry.date) : null}
                  disabled={readOnly}
                  onChange={(newValue) =>
                    handleChange(
                      index,
                      "date",
                      newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                    )
                  }
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </Grid>
              {/* Time */}
              <Grid size={4}>
                <TimePicker
                  label="Time"
                  value={
                    entry.localTime ? dayjs(entry.localTime, "HH:mm") : null
                  }
                  disabled={readOnly}
                  onChange={(newValue) =>
                    handleChange(
                      index,
                      "localTime",
                      newValue ? dayjs(newValue).format("HH:mm") : ""
                    )
                  }
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </Grid>
              {/* Remarks */}
              <Grid size={3}>
                <TextField
                  label="Remarks"
                  fullWidth
                  value={entry.remarks}
                  disabled={readOnly}
                  onChange={(e) =>
                    handleChange(index, "remarks", e.target.value)
                  }
                />
              </Grid>
            </Grid>
            {/* Divider between sections */}
            {index !== formData.logEntries.length - 1 && (
              <Divider sx={{ my: 2 }} />
            )}
          </div>
        ))}
        {/* Overall Remarks */}
        <TextField
          label="Overall Remarks"
          fullWidth
          margin="normal"
          value={formData.overallRemarks}
          disabled={readOnly}
          onChange={(e) =>
            setFormData({ ...formData, overallRemarks: e.target.value })
          }
          multiline
          rows={3}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" disabled={readOnly}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BargeLogModal;
